import React, { useState, useEffect, useRef, useCallback } from 'react';
import './channels.css';
import searchIcon from './images/icons/search-icon.svg';
import playIcon from './images/icons/play-icon.svg';
import pauseIcon from './images/icons/pause-icon.svg';
import questionIllustration from './images/question-illustration.png';
import defaultAlbumArt from './images/default-album-art.png';
import nextIcon from './images/icons/next-icon.svg';
import volumeIcon from './images/icons/volume-icon.svg';
import eyeLookIcon from './images/icons/eye-look-icon.svg';
import eyeHiddenIcon from './images/icons/eye-hidden-icon.svg';
import infoCircleIcon from './images/icons/info-circle-icon.svg';
import ChannelInfoModal from './components/ChannelInfoModal';
import DeclineConfirmModal from './components/DeclineConfirmModal';
import DeclineReplacementModal from './components/DeclineReplacementModal';
import noSearchResultsIllustration from './images/illustration_channels_no_search_results.png';
import WaveformModal from './components/WaveformModal';
import ApiService from './services/api';
import {
  peekStoredActiveMasterAccountId,
  persistActiveMasterAccountId,
} from './tenant/activeMasterAccount';
import { useOptionalTenant } from './tenant/TenantContext';
import authService from './services/authService';

const TEST_MP3 = '/static/media/default-album-art.png'; // Using local asset to avoid CORS

/** Browser listening Zone until Locations/Zones UI binds a real zone. */
function browserListenZoneId() {
  // peekStored — never clear MA storage (readStored without a token used to).
  const ma = peekStoredActiveMasterAccountId() || 'unknown';
  return `zone_browser_${ma}`;
}

function mapApiChannel(ch) {
  const id = ch.channelId || ch.id;
  return {
    id,
    channelId: id,
    name: ch.name || '',
    type: ch.type || ch.channelType || '',
    declined: Boolean(ch.declined),
    preferenceStatus: ch.preferenceStatus,
    genres: ch.genres || '',
    energyLevel: ch.energyLevel || '',
    description: ch.description || '',
    featuredArtists: ch.featuredArtists || '',
    image: ch.artworkUrl || ch.image || null,
  };
}

const Channels = () => {
  const tenantCtx = useOptionalTenant();
  const tenantReady = Boolean(tenantCtx?.isReady && tenantCtx?.tenant?.masterAccountId);
  const [filter, setFilter] = useState('approved');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedEnergyLevel, setSelectedEnergyLevel] = useState('');
  const [bulkSelect, setBulkSelect] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [isVolumeSliderVisible, setVolumeSliderVisible] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [channelToDecline, setChannelToDecline] = useState(null);
  const [waveformModalOpen, setWaveformModalOpen] = useState(false);
  const [waveformSongTitle, setWaveformSongTitle] = useState('');
  const [waveformAudioUrl, setWaveformAudioUrl] = useState('');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowPlayingTrack, setNowPlayingTrack] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const audioRef = useRef(null);
  const advancingRef = useRef(false);
  const volumeControlRef = useRef(null);
  const volumeRef = useRef(100);

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
    }
    return audioRef.current;
  }, []);

  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError(null);
    try {
      // Ensure active MA is persisted before /me/channels* (requireActiveMa).
      const maId = tenantCtx?.tenant?.masterAccountId;
      if (maId) {
        try {
          const token = await authService.getValidAccessToken();
          persistActiveMasterAccountId(maId, token);
        } catch {
          /* header injection still uses resolveActiveMasterAccountId */
        }
      }
      const res = await ApiService.listMeChannels();
      const payload = res?.data || res;
      const rows = Array.isArray(payload?.channels) ? payload.channels : [];
      setChannels(rows.map(mapApiChannel));
    } catch (err) {
      setChannelsError(err?.message || 'Failed to load channels');
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, [tenantCtx?.tenant?.masterAccountId]);

  useEffect(() => {
    // Wait for tenant bootstrap so X-KK-Active-Master-Account-Id is available.
    if (tenantCtx && !tenantReady) {
      if (tenantCtx.status === 'error') {
        setChannelsLoading(false);
        setChannelsError(
          tenantCtx.error?.message || 'Active Master Account required',
        );
      }
      return;
    }
    void loadChannels();
  }, [tenantCtx, tenantReady, loadChannels]);

  const requestPlayback = useCallback(
    async (channel, action) => {
      if (!channel?.channelId && !channel?.id) return;
      if (advancingRef.current) return;
      advancingRef.current = true;
      const channelId = channel.channelId || channel.id;
      try {
        const res = await ApiService.meChannelPlaybackNext(channelId, {
          zoneId: browserListenZoneId(),
          action,
        });
        const data = res?.data || res;
        if (!data?.streamUrl) {
          setActionError('No playable audio for this channel');
          return;
        }
        const audio = ensureAudio();
        audio.src = data.streamUrl;
        audio.muted = false;
        audio.volume = Math.min(1, Math.max(0, volumeRef.current / 100));
        setCurrentTime(0);
        setDuration(0);
        setCurrentlyPlaying(channel);
        setNowPlayingTrack({
          title: data.title || data.trackTitle || null,
          artist: data.artist || data.artistName || data.primaryArtistName || null,
          trackId: data.trackId || null,
        });
        await audio.play();
        setIsPlaying(true);
        setActionError(null);
      } catch (err) {
        setActionError(err?.message || 'Failed to play channel');
        setIsPlaying(false);
      } finally {
        advancingRef.current = false;
      }
    },
    [ensureAudio],
  );

  useEffect(() => {
    const audio = ensureAudio();
    const onEnded = () => {
      if (currentlyPlaying) {
        void requestPlayback(currentlyPlaying, 'ended');
      }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const onDurationChange = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onDurationChange);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onDurationChange);
    };
  }, [currentlyPlaying, ensureAudio, requestPlayback]);

  useEffect(() => {
    if (!isVolumeSliderVisible) return undefined;
    const onPointerDown = (event) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target)) {
        setVolumeSliderVisible(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isVolumeSliderVisible]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
      audioRef.current = null;
    };
  }, []);

  const genres = [
    'All Genres',
    'Pop',
    'Hip-Hop',
    'R&B',
    'Rap',
    'EDM',
    'Country',
    'Rock',
    'Jazz',
    'Blues',
    'Funk',
    'Soul',
    'Reggae',
    'Latin'
  ];

  const energyLevels = [
    'All Energy Levels',
    'Low',
    'Medium',
    'High'
  ];

  const handleDecline = async (channelId) => {
    setActionError(null);
    try {
      const res = await ApiService.declineMeChannel(channelId);
      const payload = res?.data || res;
      const updated = payload?.channel ? mapApiChannel(payload.channel) : null;
      setChannels((prev) =>
        prev.map((channel) =>
          channel.id === channelId
            ? updated || { ...channel, declined: true }
            : channel,
        ),
      );
    } catch (err) {
      setActionError(
        err?.message ||
          'At least one music channel must remain approved so playback can continue.',
      );
      throw err;
    }
  };

  const handleApprove = async (channelId) => {
    setActionError(null);
    try {
      const res = await ApiService.approveMeChannel(channelId);
      const payload = res?.data || res;
      const updated = payload?.channel ? mapApiChannel(payload.channel) : null;
      setChannels((prev) =>
        prev.map((channel) =>
          channel.id === channelId
            ? updated || { ...channel, declined: false }
            : channel,
        ),
      );
    } catch (err) {
      setActionError(err?.message || 'Failed to approve channel');
    }
  };

  const toggleBulkSelect = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedChannels([]);
  };

  const toggleChannelSelection = (channelId, e) => {
    e.stopPropagation();
    
    if (filter === 'approved') {
      // Keep existing logic for Approved tab
      const firstApprovedChannelId = channels
        .filter(channel => !channel.declined)[0]?.id;

      if (channelId === firstApprovedChannelId) {
        if (selectedChannels.includes(channelId)) {
          setSelectedChannels(prev => prev.filter(id => id !== channelId));
          return;
        }
        
        const approvedChannels = channels.filter(channel => !channel.declined);
        const otherChannelsSelected = selectedChannels.length;
        if (otherChannelsSelected < approvedChannels.length - 1) {
          setSelectedChannels(prev => [...prev, channelId]);
          return;
        }
        return;
      }

      if (!selectedChannels.includes(channelId)) {
        const approvedChannels = channels.filter(channel => !channel.declined);
        const wouldSelectAll = selectedChannels.length + 1 === approvedChannels.length;
        if (wouldSelectAll) {
          return;
        }
      }
    }

    // Simple toggle for Declined tab
    setSelectedChannels(prev => 
      prev.includes(channelId)
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleDeclineSelected = async () => {
    setActionError(null);
    const ids = [...selectedChannels];
    for (const channelId of ids) {
      try {
        await handleDecline(channelId);
      } catch (_) {
        break;
      }
    }
    setSelectedChannels([]);
    setBulkSelectMode(false);
  };

  const handleApproveSelected = async () => {
    setActionError(null);
    for (const channelId of selectedChannels) {
      await handleApprove(channelId);
    }
    setSelectedChannels([]);
    setBulkSelectMode(false);
  };

  const handleSelectAll = () => {
    if (!bulkSelectMode) {
      setBulkSelectMode(true);
    }
    
    if (selectedChannels.length > 0) {
      setSelectedChannels([]);
      setBulkSelectMode(false);
    } else {
      const channelsToSelect = channels
        .filter(channel => filter === 'approved' ? !channel.declined : channel.declined)
        .map(channel => channel.id);
      
      if (filter === 'approved') {
        // Keep restriction for Approved tab
        const firstChannelId = channelsToSelect[0];
        setSelectedChannels(channelsToSelect.filter(id => id !== firstChannelId));
      } else {
        // Allow selecting all for Declined tab
        setSelectedChannels(channelsToSelect);
      }
    }
  };

  const handleInfoClick = (e, channel) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedChannel(channel);
  };

  const getFilteredChannels = () => {
    return channels.filter(channel => {
      const matchesSearch = channel.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filter === 'approved' ? !channel.declined : channel.declined;
      
      // Handle genres string
      const channelGenres = channel.genres ? channel.genres.split(',').map(g => g.trim()) : [];
      
      console.log('Channel:', channel.name);
      console.log('Channel genres:', channelGenres);
      console.log('Selected genre:', selectedGenre);
      
      const matchesGenre = !selectedGenre || selectedGenre === 'All Genres' || 
        channelGenres.some(genre => genre.toLowerCase() === selectedGenre.toLowerCase());
      
      // Handle "Medium to high" type energy levels
      const channelEnergyLevels = channel.energyLevel ? 
        channel.energyLevel.toLowerCase().split(' to ').map(level => level.trim()) : 
        [];
      const matchesEnergy = !selectedEnergyLevel || selectedEnergyLevel === 'All Energy Levels' || 
        channelEnergyLevels.some(level => level === selectedEnergyLevel.toLowerCase());
      
      console.log('Matches - Search:', matchesSearch, 'Filter:', matchesFilter, 'Genre:', matchesGenre, 'Energy:', matchesEnergy);
      
      return matchesSearch && matchesFilter && matchesGenre && matchesEnergy;
    });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeclineClick = (e, channelId) => {
    e.stopPropagation();
    setChannelToDecline(channelId);
    setShowDeclineConfirm(true);
  };

  const handleConfirmDecline = () => {
    setShowDeclineConfirm(false);
    
    // Check if channel is playing in a zone (mock check for now)
    const needsReplacement = true; // This would be your actual check
    
    if (needsReplacement) {
      setShowReplacementModal(true);
    } else {
      handleDecline(channelToDecline).catch(() => {});
      setChannelToDecline(null);
    }
  };

  const handleReplacementConfirm = async () => {
    try {
      await handleDecline(channelToDecline);
      setShowReplacementModal(false);
      setChannelToDecline(null);
    } catch (_) {
      // Keep modal open so the operator can cancel; actionError is set.
    }
  };

  const handleSongClick = (channel) => {
    console.log('Song clicked:', channel.name);
    setWaveformSongTitle(channel.name);
    setWaveformAudioUrl(TEST_MP3); // Use test MP3 for now
    setWaveformModalOpen(true);
  };

  const handleTogglePlayPause = () => {
    const audio = audioRef.current;
    if (!currentlyPlaying || !audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    if (audio.src) {
      audio.play().then(() => setIsPlaying(true)).catch(() => {
        void requestPlayback(currentlyPlaying, 'start');
      });
      return;
    }
    void requestPlayback(currentlyPlaying, 'start');
  };

  /** Card play: toggle pause/resume when this channel is active; otherwise start it. */
  const handlePlayChannel = (channel) => {
    const channelId = channel?.channelId || channel?.id;
    const activeId = currentlyPlaying?.channelId || currentlyPlaying?.id;
    if (channelId && activeId && channelId === activeId) {
      handleTogglePlayPause();
      return;
    }
    void requestPlayback(channel, 'start');
  };

  const isChannelActivelyPlaying = (channel) => {
    const channelId = channel?.channelId || channel?.id;
    const activeId = currentlyPlaying?.channelId || currentlyPlaying?.id;
    return Boolean(channelId && activeId && channelId === activeId && isPlaying);
  };

  const handleSkip = () => {
    if (!currentlyPlaying) return;
    void requestPlayback(currentlyPlaying, 'skip');
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const handleVolumeChange = (event) => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    const clamped = Math.min(100, Math.max(0, next));
    volumeRef.current = clamped;
    setVolume(clamped);
    const audio = audioRef.current || ensureAudio();
    audio.volume = clamped / 100;
    audio.muted = false;
  };

  const handleVolumeButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setVolumeSliderVisible((visible) => !visible);
  };

  const renderDeclinedContent = () => {
    const declinedChannels = getFilteredChannels();

    if (declinedChannels.length === 0) {
      if (searchQuery) {
        return (
          <div className="declined-content">
            <div className="declined-message">
              <img 
                src={noSearchResultsIllustration} 
                alt="No Search Results" 
                className="question-illustration" 
              />
              <div className="declined-text">
                <p className="no-results">Your search returned no results</p>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div className="declined-content">
          <div className="declined-message">
            <img 
              src={questionIllustration} 
              alt="Question" 
              className="question-illustration" 
            />
            <div className="declined-text">
              <p>You currently do not have any channels marked as declined. To set a channel as declined, visit the <button className="link-button" onClick={() => setFilter('approved')}>Approved Channels</button> view and decline the channel you choose.</p>
              
              <div className="questions-section">
                <h3>Questions?</h3>
                <div className="question-item">
                  <button 
                    className="question-button"
                    onClick={() => setIsQuestionExpanded(!isQuestionExpanded)}
                  >
                    <span className="plus-icon">
                      {isQuestionExpanded ? '-' : '+'}
                    </span>
                    What does it mean to decline a channel?
                  </button>
                  {isQuestionExpanded && (
                    <p className="question-answer">
                      When declining a channel, the channel artwork will be hidden from your devices and it will no longer be available to be set/played within your locations.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="channels-page-grid">
        {declinedChannels.map(channel => (
          <div 
            key={channel.id} 
            className={`channels-page-card ${channel.type === 'KK Original' ? 'kk-logo' : ''} ${isChannelActivelyPlaying(channel) ? 'is-playing' : ''}`}
            onClick={() => handleInfoClick(null, channel)}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={infoCircleIcon} 
              alt="info" 
              className="info-circle-icon"
              onClick={(e) => handleInfoClick(e, channel)}
            />
            {bulkSelectMode && (
              <div className="channel-checkbox">
                <input
                  type="checkbox"
                  checked={selectedChannels.includes(channel.id)}
                  onChange={(e) => toggleChannelSelection(channel.id, e)}
                />
              </div>
            )}
            <div className="channel-image">
              <img 
                src={channel.image || defaultAlbumArt} 
                alt={channel.name} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultAlbumArt;
                }}
              />
              <img 
                src={eyeHiddenIcon} 
                alt="hidden" 
                className="eye-hidden-icon-right"
              />
              <div className="channel-hover-overlay">
                <button 
                  className="channel-action-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayChannel(channel);
                  }}
                  aria-label={isChannelActivelyPlaying(channel) ? 'Pause' : 'Play'}
                >
                  <img
                    src={isChannelActivelyPlaying(channel) ? pauseIcon : playIcon}
                    alt={isChannelActivelyPlaying(channel) ? 'Pause' : 'Play'}
                  />
                </button>
                <button 
                  className="channel-action-button decline-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(channel.id);
                  }}
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };


  return (
    <div className="channels-page-container">
      {(channelsError || actionError) && (
        <div
          className="channels-api-error"
          role="alert"
          style={{
            margin: '12px 16px 0',
            padding: '10px 12px',
            color: '#7a1f1f',
            background: '#fdecec',
            border: '1px solid #f0c2c2',
          }}
        >
          {actionError || channelsError}
        </div>
      )}
      {channelsLoading && (
        <div style={{ margin: '12px 16px 0' }}>Loading channels…</div>
      )}
      <h1>Select your music channels</h1>
      <p className="channels-subtitle">Preview and approve/decline the music channels that will be available to be played within your locations.</p>

      <div className="channels-header">
        <div className="filter-tabs">
          <button 
            className={`tab ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            <img src={eyeLookIcon} alt="eye" className="icon" /> Approved
          </button>
          <button 
            className={`tab ${filter === 'declined' ? 'active' : ''}`}
            onClick={() => setFilter('declined')}
          >
            <img src={eyeHiddenIcon} alt="eye hidden" className="icon" /> Declined
          </button>
        </div>

        <div className="channel-filters">
          <div className="filter-group">
            <select 
              className="filter-select"
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
            >
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
            <select 
              className="filter-select"
              value={selectedEnergyLevel}
              onChange={(e) => setSelectedEnergyLevel(e.target.value)}
            >
              {energyLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
          <div className="search-wrapper">
            <img src={searchIcon} alt="search" className="search-icon" />
            <input 
              type="text" 
              placeholder="Search channels..." 
              className="channel-search"
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <div className="selection-controls">
            <button 
              className={`bulk-select-button ${bulkSelectMode && !selectedChannels.length ? 'active' : ''}`}
              onClick={toggleBulkSelect}
            >
              Bulk Select
            </button>
            <button 
              className={`select-all-button ${!bulkSelectMode && selectedChannels.length > 0 ? 'active' : ''}`}
              onClick={handleSelectAll}
            >
              Select All
            </button>
            {bulkSelectMode && selectedChannels.length > 0 && (
              <button 
                className="decline-selected-button"
                onClick={filter === 'approved' ? handleDeclineSelected : handleApproveSelected}
              >
                {filter === 'approved' ? 'Decline Selected' : 'Approve Selected'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="channels-section">
        <div className="channels-section-header">
          <h2>{filter === 'approved' ? 'Approved' : 'Declined'} Channels</h2>
          {filter === 'approved' && selectedChannels.length > 0 && (
            <span className="approval-note">Note: one channel must be approved at all times</span>
          )}
        </div>
        {filter === 'approved' ? (
          <>
            {getFilteredChannels().length === 0 ? (
              <div className="declined-content">
                <div className="declined-message">
                  <img 
                    src={noSearchResultsIllustration} 
                    alt="No Search Results" 
                    className="question-illustration" 
                  />
                  <div className="declined-text">
                    <p className="no-results">Your search returned no results</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="channels-page-grid">
                {getFilteredChannels().map(channel => (
                  <div
                    key={channel.id}
                    className={`channels-page-card ${channel.type === 'KK Original' ? 'kk-logo' : ''} ${isChannelActivelyPlaying(channel) ? 'is-playing' : ''}`}
                    onClick={() => handleInfoClick(null, channel)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={infoCircleIcon} 
                      alt="info" 
                      className="info-circle-icon"
                      onClick={(e) => handleInfoClick(e, channel)}
                    />
                    {bulkSelectMode && (
                      <div className="channel-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedChannels.includes(channel.id)}
                          onChange={(e) => toggleChannelSelection(channel.id, e)}
                        />
                      </div>
                    )}
                    <div className="channel-image">
                      <img 
                        src={channel.image || defaultAlbumArt} 
                        alt={channel.name} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultAlbumArt;
                        }}
                      />
                      <div className="channel-hover-overlay">
                        <button 
                          className="channel-action-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayChannel(channel);
                          }}
                          aria-label={isChannelActivelyPlaying(channel) ? 'Pause' : 'Play'}
                        >
                          <img
                            src={isChannelActivelyPlaying(channel) ? pauseIcon : playIcon}
                            alt={isChannelActivelyPlaying(channel) ? 'Pause' : 'Play'}
                          />
                        </button>
                        <button 
                          className="channel-action-button decline-button"
                          onClick={(e) => handleDeclineClick(e, channel.id)}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          renderDeclinedContent()
        )}
      </div>

      <div className="now-playing-bar">
        <div className="now-playing-info">
          {currentlyPlaying ? (
            <>
              <div className="now-playing-artwork">
                <img 
                  src={currentlyPlaying.image || defaultAlbumArt} 
                  alt={nowPlayingTrack?.title || currentlyPlaying.name}
                />
              </div>
              <div className="now-playing-details">
                <div
                  className="now-playing-title"
                  title={
                    nowPlayingTrack?.title && nowPlayingTrack?.artist
                      ? `${nowPlayingTrack.artist} — ${nowPlayingTrack.title}`
                      : (nowPlayingTrack?.title || currentlyPlaying.name)
                  }
                >
                  {nowPlayingTrack?.title || currentlyPlaying.name}
                </div>
                <div
                  className="now-playing-subtitle"
                  title={nowPlayingTrack?.artist || undefined}
                >
                  {nowPlayingTrack?.artist || 'Unknown artist'}
                </div>
              </div>
            </>
          ) : (
            <div className="now-playing-details">
              <div className="now-playing-title">No channel playing</div>
            </div>
          )}
        </div>
        <div className="playback-controls">
          <button 
            className="play-button"
            onClick={handleTogglePlayPause}
            disabled={!currentlyPlaying}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <img
              src={isPlaying ? pauseIcon : playIcon}
              alt={isPlaying ? 'Pause' : 'Play'}
            />
          </button>
          <button
            className="play-button"
            disabled={!currentlyPlaying}
            onClick={handleSkip}
          >
            <img src={nextIcon} alt="Next" />
          </button>
        </div>
        <div className="time-control">
          <span className="time-display">{formatTime(currentTime)}</span>
          <input 
            type="range" 
            className="time-slider" 
            min="0" 
            max={duration > 0 ? duration : 0}
            step="0.1"
            value={duration > 0 ? Math.min(currentTime, duration) : 0}
            disabled={!currentlyPlaying || duration <= 0}
            onChange={handleSeek}
            style={{
              background: duration > 0
                ? `linear-gradient(to right, rgba(255,255,255,0.9) ${(currentTime / duration) * 100}%, rgba(255,255,255,0.25) ${(currentTime / duration) * 100}%)`
                : 'rgba(255,255,255,0.25)',
            }}
            aria-label="Seek"
          />
          <span className="time-display">{formatTime(duration)}</span>
        </div>
        <div className="volume-control" ref={volumeControlRef}>
          {isVolumeSliderVisible && (
            <div className="volume-slider-popover" onClick={(e) => e.stopPropagation()}>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={volume}
                className="volume-slider"
                aria-label="Volume level"
                onChange={handleVolumeChange}
                onInput={handleVolumeChange}
                style={{
                  background: `linear-gradient(to right, rgba(255,255,255,0.9) ${volume}%, rgba(255,255,255,0.25) ${volume}%)`,
                }}
              />
            </div>
          )}
          <button
            type="button"
            className="volume-icon-button"
            aria-label="Volume"
            aria-expanded={isVolumeSliderVisible}
            onClick={handleVolumeButtonClick}
          >
            <img
              src={volumeIcon}
              alt=""
              className="volume-icon"
            />
          </button>
        </div>
      </div>

      {selectedChannel && (
        <ChannelInfoModal 
          channel={selectedChannel} 
          onClose={() => setSelectedChannel(null)}
        />
      )}

      {showDeclineConfirm && (
        <DeclineConfirmModal
          onClose={() => {
            setShowDeclineConfirm(false);
            setChannelToDecline(null);
          }}
          onConfirm={handleConfirmDecline}
        />
      )}

      {showReplacementModal && (
        <DeclineReplacementModal
          onClose={() => {
            setShowReplacementModal(false);
            setChannelToDecline(null);
          }}
          onConfirm={handleReplacementConfirm}
          channels={channels}
          channelToDecline={channelToDecline}
        />
      )}

      <WaveformModal
        open={waveformModalOpen}
        onClose={() => setWaveformModalOpen(false)}
        songTitle={waveformSongTitle}
        audioUrl={waveformAudioUrl}
      />
    </div>
  );
};

export default Channels;